/**
 * Phase 0.5 Component F — TanStack Query hook backing every UI primitive.
 *
 * `useViewProjection(resource, action?)` returns the resolver's PolicyResult
 * for the current principal + the named resource + action. Surfaces
 * never construct authz logic themselves — they consume this hook (or
 * the `<MaskedField>` / `<NoViewSlot>` / `<ActionGate>` wrappers built
 * on top).
 *
 * Cache strategy: keyed on `(principal-tenant, principal-actor, kind, id, action)`. The
 * resolver lookup is cheap (<25ms target) but a workspace page may
 * fire dozens per render, so deduplication matters. TTL is short (30s
 * staleTime) because role grants change at admin-action speed and we
 * want the next page to resourcelect them.
 */ import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/authContext';
import { authzClient } from '../connect';
import { FAIL_CLOSED } from './types';
export const VIEW_PROJECTION_KEY = 'access:viewProjection';
export function useViewProjection(resource, { action = 'read', enabled = true } = {}) {
    const { user } = useAuth();
    const hasIdentity = !!user?.tenant_id && !!user?.id;
    const query = useQuery({
        queryKey: [
            VIEW_PROJECTION_KEY,
            user?.tenant_id,
            user?.id,
            resource.kind,
            resource.id,
            action,
            resource.objectType,
            resource.objectId
        ],
        enabled: enabled && hasIdentity && !!resource.id && !!resource.kind,
        staleTime: 30000,
        queryFn: async ()=>{
            // The Connect transport carries the JWT cookie via sabaody, so
            // the resolver pulls the principal off the gateway-set headers.
            // We only need to ship the resource + action shape here.
            const resp = await authzClient.resolve({
                principal: {
                    // Server fills these from the validated JWT — sending the
                    // empty shape keeps the proto happy without leaking client
                    // assumptions about the principal.
                    id: '',
                    tenantId: ''
                },
                resource: {
                    kind: resource.kind,
                    id: resource.id,
                    tenantId: '',
                    attr: {}
                },
                action,
                objectType: resource.objectType ?? '',
                objectId: resource.objectId ?? '',
                sensitivity: resource.sensitivity ?? ''
            });
            if (!resp.policy) {
                return FAIL_CLOSED;
            }
            return {
                decision: resp.policy.decision || 'deny',
                viewMode: resp.policy.viewMode || 'no_view',
                maskedFields: resp.policy.maskedFields ?? [],
                actionBlocked: resp.policy.actionBlocked ?? [],
                reason: resp.policy.reason ?? '',
                matchedPolicyId: resp.policy.matchedPolicyId ?? '',
                auditRequired: resp.policy.auditRequired ?? false,
                grantedActions: resp.policy.grantedActions ?? []
            };
        }
    });
    return {
        ...query,
        data: hasIdentity && !query.isError && !query.isFetching ? query.data : undefined
    };
}
